const COMMON_PASSWORDS = [
    '12345678',
    'password',
    'qwerty',
    'patient123',
    '123456',
    '123456789',
    '1234567890',
    'admin',
    'welcome',
    'admin123'
];

export const PASSWORD_REUSE_ERROR_MESSAGE =
  'You cannot reuse any of your last 3 passwords. Please choose a new password.';

/** Extract a user-facing message from API / thunk errors. */
export function extractSubmissionErrorMessage(error, fallback = '') {
  if (typeof error === 'string' && error.trim()) return error.trim();

  // Redux thunk reject payloads: { message, field, errors }
  if (error && typeof error === 'object' && !error.response) {
    const payloadMsg = error.message;
    if (typeof payloadMsg === 'string' && payloadMsg.trim()) return payloadMsg.trim();
  }

  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();

  const candidates = [
    data?.message,
    data?.error,
    Array.isArray(data?.errors) ? data.errors[0] : null,
    error?.message,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }

  return fallback;
}

/**
 * Apply server password validation errors on the password field (not toast).
 * @returns {boolean} true when a password field error was set
 */
export function applyPasswordServerFieldError(setErrors, errorOrMessage) {
  const message = typeof errorOrMessage === 'string'
    ? errorOrMessage.trim()
    : extractSubmissionErrorMessage(errorOrMessage, '');

  if (!message || !message.toLowerCase().includes('password')) {
    return false;
  }

  setErrors((prev) => ({ ...prev, password: message }));
  return true;
}

/** User-facing string for Redux/API errors that may be a string or { message, field, errors }. */
export function formatDisplayError(errorOrPayload, fallback = '') {
  return extractSubmissionErrorMessage(errorOrPayload, fallback);
}

export const validatePassword = (password, userData = {}) => {
    if (!password) {
        return { isValid: false, error: 'Password is required.' };
    }

    // 1. Length Check
    if (password.length < 8) {
        return { isValid: false, error: 'Password must be at least 8 characters long.' };
    }
    if (password.length > 64) {
        return { isValid: false, error: 'Password must be less than 64 characters.' };
    }

    // 2. Common Passwords Check
    if (COMMON_PASSWORDS.includes(password.toLowerCase())) {
        return { isValid: false, error: 'This password is too common and cannot be used.' };
    }

    // 3. Complexity Check
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*]/.test(password);

    if (!hasUpperCase) {
        return { isValid: false, error: 'Password must contain at least one uppercase letter.' };
    }
    if (!hasLowerCase) {
        return { isValid: false, error: 'Password must contain at least one lowercase letter.' };
    }
    if (!hasNumber) {
        return { isValid: false, error: 'Password must contain at least one number.' };
    }
    if (!hasSpecialChar) {
        return { isValid: false, error: 'Password must contain at least one special character (!@#$%^&*).' };
    }

    // 4. User Data Check (Name/Birthdate)
    if (userData.firstName && password.toLowerCase().includes(userData.firstName.toLowerCase())) {
        return { isValid: false, error: 'Password cannot contain your first name.' };
    }
    if (userData.lastName && password.toLowerCase().includes(userData.lastName.toLowerCase())) {
        return { isValid: false, error: 'Password cannot contain your last name.' };
    }
    if (userData.dob && password.includes(userData.dob)) { // Simple string check, format dependent
        return { isValid: false, error: 'Password cannot contain your birth date.' };
    }

    return { isValid: true, error: null };
};
